import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type {
  ProductPageKey,
  ProductPagePayload,
} from "../../shared/contracts/product-pages.js";
import { loadProductPage } from "../api/product-page-api.js";
import {
  ProductPageCache,
  type ProductPageScope,
} from "./product-page-cache.js";

export interface ProductPageState {
  data: ProductPagePayload | null;
  /** True only while nothing at all can be shown yet. */
  loading: boolean;
  /** True while the page for the current route is still on its way. */
  pending: boolean;
  error: string;
  reload(): void;
}

/**
 * Last payload per page. A visited tab reopens at once from here while a
 * fresh copy loads in the background (stale-while-revalidate), and the
 * primary tabs are prefetched after sign-in so the first tap is instant.
 */
const cache = new ProductPageCache();

export function selectProductPageAccount(accountId: string | null): void {
  cache.selectAccount(accountId);
}

export function clearProductPageCache(): void {
  cache.clear();
}

/** Warms the cache for the given pages when the browser is idle. */
export function prefetchProductPages(
  accountId: string,
  pages: ProductPageKey[],
): void {
  const run = cache.prefetch(accountId, pages, loadProductPage);
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 300);
  }
}

/**
 * Swaps to another page as one cross-fade (View Transitions API) instead
 * of a hard cut. Falls back to a plain update where the API is missing or
 * motion is reduced.
 */
function transition(apply: () => void): void {
  const reduced = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  if (reduced || typeof document.startViewTransition !== "function") {
    apply();
    return;
  }
  const transition = document.startViewTransition(() => {
    flushSync(apply);
  });
  // A second swap before this one animates (cached payload, then the fresh
  // one) skips it; the update has still applied, so the rejection is noise.
  transition.ready.catch(() => undefined);
}

export function useProductPage(
  accountId: string,
  page: ProductPageKey,
  enabled = true,
): ProductPageState {
  const scope = cache.selectAccount(enabled ? accountId : null);
  const [state, setState] = useState<{
    scope: ProductPageScope;
    data: ProductPagePayload | null;
    error: string;
  }>(() => ({ scope, data: cache.get(scope, page), error: "" }));
  const data = state.scope === scope && enabled ? state.data : null;
  const error = state.scope === scope && enabled ? state.error : "";
  const shown = useRef(data);
  shown.current = data;
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    let active = true;
    let displayRevision = 0;
    if (!enabled) {
      setState({ scope, data: null, error: "" });
      return () => {
        active = false;
      };
    }
    setState({ scope, data: shown.current, error: "" });
    // Same page: replace quietly. Another page: cross-fade to it.
    const show = (payload: ProductPagePayload) => {
      const version = ++displayRevision;
      const apply = () => {
        if (active && version === displayRevision && cache.isCurrent(scope))
          setState({ scope, data: payload, error: "" });
      };
      if (shown.current?.page === payload.page) apply();
      else transition(apply);
    };
    const cached = cache.get(scope, page);
    if (cached && cached !== shown.current) show(cached);
    void cache
      .load(scope, page, loadProductPage)
      .then((payload) => {
        if (!active || !payload) return;
        show(payload);
      })
      .catch((cause: unknown) => {
        if (!active || !cache.isCurrent(scope) || cache.get(scope, page))
          return;
        displayRevision += 1;
        setState({
          scope,
          data: null,
          error:
            cause instanceof Error
              ? cause.message
              : "This page could not load.",
        });
      });
    return () => {
      active = false;
    };
  }, [enabled, page, revision, scope]);
  return {
    data,
    loading: enabled && data === null && error === "",
    pending: enabled && error === "" && data?.page !== page,
    error,
    reload,
  };
}
